import type { RoomJoinRequest, RoomRequestListQuery } from '@pulsify/shared/dto'
import { LiveRoomErrorCode } from '@pulsify/shared/errors'
import { instagramHandle, linkedinUrl } from '@pulsify/shared/validation'
import { Link } from '@tanstack/react-router'
import { Check, Inbox, Instagram, Linkedin, Lock, X } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import { useEvent } from '~/entities/event/model/use-events'
import { placesLimit, requestFits } from '~/entities/room-request/lib/request-places'
import {
  useAnswerRoomRequest,
  useRoomRequests,
} from '~/entities/room-request/model/use-room-requests'
import * as m from '~/paraglide/messages'
import { getLocale } from '~/paraglide/runtime.js'
import { extractApiError, getErrorMessageFromError } from '~/shared/lib/api-errors'
import { Avatar, AvatarFallback } from '~/shared/ui/avatar'
import { Button } from '~/shared/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/shared/ui/card'
import { SegmentedControl } from '~/shared/ui/segmented-control'
import { Skeleton } from '~/shared/ui/skeleton'

type RequestStatus = RoomRequestListQuery['status']

const NOTE_PREVIEW_CHARS = 90

function askedOn(iso: string): string {
  return new Date(iso).toLocaleString(getLocale(), { dateStyle: 'medium', timeStyle: 'short' })
}

/** The requests to join one event on request, answered with ✓ / ✗. */
export function EventRequestsPanel({ eventId }: { eventId: string }) {
  const { data: business } = useCurrentBusiness()
  const businessId = business?.id ?? ''
  const { data: event } = useEvent(businessId, eventId)
  const [status, setStatus] = useState<RequestStatus>('pending')
  const requests = useRoomRequests(businessId, eventId, status)
  const counts = requests.data?.counts
  const acceptedPeople = requests.data?.acceptedPeople ?? 0
  const capacity = requests.data?.capacity ?? null
  const onRequest = event?.roomVisibility === 'request'

  const options = [
    { value: 'pending', label: withCount(m.requestsPending(), counts?.pending) },
    { value: 'accepted', label: withCount(m.requestsAccepted(), counts?.accepted) },
    { value: 'declined', label: withCount(m.requestsDeclined(), counts?.declined) },
  ] as const

  return (
    <div className="space-y-6">
      {event && !onRequest ? (
        <Card>
          <CardContent className="flex flex-col items-start gap-3 pt-6 sm:flex-row sm:items-center sm:justify-between">
            <p className="flex items-start gap-2 text-sm text-muted-foreground">
              <Lock className="mt-0.5 size-4 shrink-0" />
              {m.requestsNotOnRequest()}
            </p>
            <Button asChild variant="outline" size="sm">
              <Link to="/dashboard/events/$eventId/edit" params={{ eventId }}>
                {m.requestsMakeOnRequest()}
              </Link>
            </Button>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader className="gap-3 sm:flex-row sm:items-start sm:justify-between sm:space-y-0">
          <div className="space-y-1.5">
            <CardTitle className="flex items-center gap-2">
              <Inbox className="size-5" />
              {m.eventRequests()}
            </CardTitle>
            <CardDescription>{m.eventRequestsDescription()}</CardDescription>
            {requests.data ? (
              <p className="text-sm font-medium">{placesLine(acceptedPeople, capacity)}</p>
            ) : null}
          </div>
          <SegmentedControl
            label={m.eventRequests()}
            value={status}
            options={options}
            onChange={setStatus}
          />
        </CardHeader>
        <CardContent>
          {requests.isLoading || !business ? (
            <div className="space-y-3">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          ) : requests.isError ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              {getErrorMessageFromError(requests.error)}
            </p>
          ) : (requests.data?.requests.length ?? 0) === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">{m.requestsEmpty()}</p>
          ) : (
            <ul className="divide-y">
              {requests.data?.requests.map((request) => (
                <RequestRow
                  key={request.id}
                  request={request}
                  businessId={businessId}
                  eventId={eventId}
                  fits={requestFits(request.companions, acceptedPeople, capacity)}
                />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function placesLine(acceptedPeople: number, capacity: number | null): string {
  const limit = placesLimit(capacity)
  if (limit !== null) {
    return m.requestsPlacesTaken({ taken: String(acceptedPeople), capacity: String(limit) })
  }
  return acceptedPeople === 1
    ? m.requestsPeopleAcceptedOne()
    : m.requestsPeopleAccepted({ count: String(acceptedPeople) })
}

function withCount(label: string, count: number | undefined): string {
  return count ? `${label} · ${count}` : label
}

function RequestRow({
  request,
  businessId,
  eventId,
  fits,
}: {
  request: RoomJoinRequest
  businessId: string
  eventId: string
  fits: boolean
}) {
  const answer = useAnswerRoomRequest(businessId, eventId)
  const [expanded, setExpanded] = useState(false)
  const name = request.displayName ?? request.firstName
  const instagram = request.instagram ? instagramHandle(request.instagram) : null
  const linkedin = request.linkedin ? linkedinUrl(request.linkedin) : null
  const note = request.note
  const longNote = !!note && note.length > NOTE_PREVIEW_CHARS

  const reply = (next: 'accept' | 'decline') =>
    answer.mutate(
      { requestId: request.id, answer: next },
      {
        onSuccess: () =>
          next === 'accept'
            ? toast.success(m.requestAcceptedToast({ name: request.firstName }))
            : toast.success(m.requestDeclinedToast()),
        onError: (error) =>
          extractApiError(error).errorCode === LiveRoomErrorCode.NoPlacesLeft
            ? toast.error(m.requestNoPlacesLeft())
            : toast.error(m.requestAnswerFailed(), {
                description: getErrorMessageFromError(error),
              }),
      },
    )

  return (
    <li className="flex flex-col gap-3 py-4 sm:flex-row sm:items-start">
      <div className="flex min-w-0 flex-1 gap-3">
        <Avatar className="size-11">
          {request.avatarUrl ? (
            <img src={request.avatarUrl} alt="" className="size-full object-cover" />
          ) : (
            <AvatarFallback className="font-medium">
              {request.firstName.slice(0, 1).toUpperCase()}
            </AvatarFallback>
          )}
        </Avatar>
        <div className="min-w-0 flex-1 space-y-1">
          <p className="truncate font-medium">
            {name}
            {request.companions > 0 ? (
              <span
                className="ml-1.5 rounded-full bg-muted px-1.5 py-0.5 text-xs font-medium text-muted-foreground"
                title={m.requestCompanionsLabel({ count: String(request.companions) })}
              >
                <span aria-hidden="true">
                  {m.requestCompanions({ count: String(request.companions) })}
                </span>
                <span className="sr-only">
                  {m.requestCompanionsLabel({ count: String(request.companions) })}
                </span>
              </span>
            ) : null}
            {request.age == null ? null : (
              <span className="font-normal text-muted-foreground">
                {' · '}
                {m.requestAge({ age: String(request.age) })}
              </span>
            )}
          </p>
          {note ? (
            <p className="whitespace-pre-line text-sm break-words">
              {longNote && !expanded ? `${note.slice(0, NOTE_PREVIEW_CHARS).trimEnd()}…` : note}
              {longNote ? (
                <button
                  type="button"
                  onClick={() => setExpanded((open) => !open)}
                  className="ml-1 font-medium text-brand-subtle-foreground underline-offset-2 hover:underline"
                >
                  {expanded ? m.requestShowLess() : m.requestShowMore()}
                </button>
              ) : null}
            </p>
          ) : null}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <span>{m.requestAskedOn({ date: askedOn(request.createdAt) })}</span>
            {instagram ? (
              <a
                href={`https://www.instagram.com/${instagram}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 hover:text-foreground"
              >
                <Instagram className="size-3.5" />@{instagram}
              </a>
            ) : null}
            {linkedin ? (
              <a
                href={linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 hover:text-foreground"
              >
                <Linkedin className="size-3.5" />
                {m.requestLinkedin()}
              </a>
            ) : null}
          </div>
        </div>
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-2 sm:pt-1">
        {request.status !== 'accepted' && !fits ? (
          <span id={`no-place-${request.id}`} className="text-xs text-muted-foreground">
            {m.requestNoPlacesLeft()}
          </span>
        ) : null}
        {request.status === 'accepted' ? null : (
          <Button
            size="sm"
            disabled={answer.isPending || !fits}
            aria-describedby={fits ? undefined : `no-place-${request.id}`}
            onClick={() => reply('accept')}
            aria-label={m.requestAcceptName({ name })}
          >
            <Check className="size-4" />
            {m.requestAccept()}
          </Button>
        )}
        {request.status === 'pending' ? (
          <Button
            size="sm"
            variant="outline"
            disabled={answer.isPending}
            onClick={() => reply('decline')}
            aria-label={m.requestDeclineName({ name })}
          >
            <X className="size-4" />
            {m.requestDecline()}
          </Button>
        ) : null}
      </div>
    </li>
  )
}
